/** Unit frame: name, HP with absorb overlay, primary resource, cast bar and aura chips. */
import type { ContentRegistry, DeepReadonly, Unit, World } from '@keystone/sim';
import { resourceColor, unitColor } from '../render/palette';
import { el, pct, setClass, setStyle, setText } from './dom';
import { shortNumber } from './format';
import { hudNow, shownCast } from '../game/prediction';
import { attachAbilityTooltip } from './tooltip/view';
import { type TooltipLive, castTooltipModel } from './tooltip/hud';
import { AuraRow } from './kit';

export interface CastBarView {
  root: HTMLElement;
  update(u: DeepReadonly<Unit> | undefined, world: DeepReadonly<World>, content: ContentRegistry): void;
}

/** Cast bar shared by unit frames and nameplates. Uninterruptible casts get the `locked` style. */
export function createCastBar(parent: HTMLElement, cls = ''): CastBarView {
  const root = el('div', `castbar ${cls}`, parent);
  const fill = el('div', 'castbar-fill', root);
  const label = el('div', 'castbar-label', root);
  const time = el('div', 'castbar-time', root);
  let tip: { live: TooltipLive; unitId: string } | null = null;
  attachAbilityTooltip(root, () => (tip ? castTooltipModel(tip.live, tip.unitId) : null));
  return {
    root,
    update(u, world, content) {
      tip = u ? { live: { world, content, playerId: null }, unitId: u.id } : null;
      // The sim's cast, or the local player's predicted one (shown on the key press).
      const c = u?.alive ? shownCast(world, u.id) : null;
      setClass(root, 'active', !!c);
      if (!c) return;
      // Sub-tick clock: the bar fills smoothly every frame instead of in 50 ms steps.
      const now = hudNow(world);
      const total = Math.max(1, c.endsAt - c.startedAt);
      const done = Math.max(0, (now - c.startedAt) / total);
      // Channels drain, casts fill.
      setStyle(fill, 'width', pct(c.kind === 'channel' ? 1 - done : done));
      setClass(root, 'locked', !c.interruptible);
      setClass(root, 'channel', c.kind === 'channel');
      const name = content.ability(c.abilityId)?.name ?? c.abilityId;
      const tgt = c.targetId && c.targetId !== u?.id ? world.units[c.targetId]?.name : undefined;
      setText(label, tgt ? `${name} → ${tgt}` : name);
      setText(time, `${Math.max(0, (c.endsAt - now) / 1000).toFixed(1)}`);
    },
  };
}

export function absorbTotal(u: DeepReadonly<Unit>): number {
  let s = 0;
  for (const a of u.auras) s += a.absorbRemaining ?? 0;
  return s;
}

export interface UnitFrameOptions {
  size: 'large' | 'small';
  showCast?: boolean;
  showAuras?: boolean;
}

export class UnitFrame {
  readonly root: HTMLElement;
  unitId: string | null = null;
  private readonly name: HTMLElement;
  private readonly hpText: HTMLElement;
  private readonly hpFill: HTMLElement;
  private readonly absorbFill: HTMLElement;
  private readonly resBar: HTMLElement;
  private readonly resFill: HTMLElement;
  private readonly resText: HTMLElement;
  private readonly pips: HTMLElement;
  private readonly cast: CastBarView | null;
  /** Real-icon aura row (kit AuraRow: pooled, describeAura tooltips). */
  private readonly auras: AuraRow | null;

  constructor(parent: HTMLElement, opts: UnitFrameOptions) {
    this.root = el('div', `uframe ${opts.size}`, parent);
    const top = el('div', 'uframe-top', this.root);
    this.name = el('div', 'uframe-name', top);
    this.hpText = el('div', 'uframe-hptext', top);
    const hp = el('div', 'bar hp', this.root);
    this.hpFill = el('div', 'bar-fill', hp);
    this.absorbFill = el('div', 'bar-absorb', hp);
    this.resBar = el('div', 'bar res', this.root);
    this.resFill = el('div', 'bar-fill', this.resBar);
    this.resText = el('div', 'bar-text', this.resBar);
    this.pips = el('div', 'pips', this.root);
    this.cast = opts.showCast ? createCastBar(this.root) : null;
    this.auras = opts.showAuras ? new AuraRow(this.root, { max: 10, className: 'auras' }) : null;
  }

  private shownId: string | null = null;

  /** Brief "target changed" flash (Web Animations: no reflow). */
  flash(): void {
    this.root.animate?.([{ filter: 'brightness(1.8)', transform: 'scale(1.03)' }, { filter: 'brightness(1)', transform: 'scale(1)' }], { duration: 180, easing: 'cubic-bezier(.2,.8,.2,1)' });
  }

  update(world: DeepReadonly<World>, content: ContentRegistry, opts: { selected?: boolean; hovered?: boolean } = {}): void {
    const changed = this.unitId !== this.shownId;
    this.shownId = this.unitId;
    setClass(this.root, 'snap', changed);
    const u = this.unitId ? world.units[this.unitId] : undefined;
    setClass(this.root, 'empty', !u);
    if (!u) return;
    setClass(this.root, 'dead', !u.alive);
    setClass(this.root, 'selected', !!opts.selected);
    setClass(this.root, 'hovered', !!opts.hovered);
    setClass(this.root, 'enemy', u.faction === 'enemy');
    setText(this.name, u.name);
    setStyle(this.name, 'color', unitColor(u));
    const frac = u.maxHp > 0 ? u.hp / u.maxHp : 0;
    setStyle(this.hpFill, 'width', pct(frac));
    const absorb = absorbTotal(u);
    setStyle(this.absorbFill, 'left', pct(frac));
    setStyle(this.absorbFill, 'width', pct(Math.min(1 - frac, absorb / Math.max(1, u.maxHp))));
    setText(this.hpText, u.alive ? `${shortNumber(u.hp)}${absorb > 0 ? ` (+${shortNumber(absorb)})` : ''}  ${Math.round(frac * 100)}%` : 'Dead');

    const cls = u.classId ? content.class(u.classId) : undefined;
    const resId = cls?.resources[0]?.id ?? (Object.keys(u.resources)[0] as string | undefined);
    const pool = resId ? u.resources[resId] : undefined;
    const usePips = !!pool && pool.max <= 6;
    setClass(this.resBar, 'hidden', !pool || usePips);
    setClass(this.pips, 'hidden', !usePips);
    if (pool && resId) {
      const color = resourceColor(resId);
      if (usePips) this.renderPips(pool.cur, pool.max, color);
      else {
        setStyle(this.resFill, 'width', pct(pool.cur / Math.max(1, pool.max)));
        setStyle(this.resFill, 'background', color);
        setText(this.resText, `${Math.floor(pool.cur)}`);
      }
    }
    this.cast?.update(u, world, content);
    this.auras?.update(world, content, u, null, hudNow(world));
  }

  private renderPips(cur: number, max: number, color: string): void {
    while (this.pips.childElementCount < max) el('div', 'pip', this.pips);
    while (this.pips.childElementCount > max) this.pips.lastElementChild!.remove();
    const full = Math.floor(cur);
    for (let i = 0; i < this.pips.children.length; i++) setStyle(this.pips.children[i] as HTMLElement, 'background', i < full ? color : 'transparent');
  }
}
